import { useState, useEffect } from 'react';

const N8N_URL = 'https://n8n.automationtekleabsam.com/webhook';
const CACHE_KEY = 'cachedDeckNames';

/**
 * Hook pour charger tous les decks disponibles.
 *
 * Stratégie (par ordre de fiabilité) :
 * 1. load-all-cards  → extrait card.deck de TOUTES les cartes (source la plus complète)
 * 2. load-decks      → decks avec stats (source secondaire)
 * 3. Cache localStorage → decks utilisés lors des sessions précédentes
 * 4. Liste par défaut → fallback si tout échoue
 */
const DEFAULT_DECKS = [
  '📚 Vocabulaire Général',
  '🗣️ Conversation de base',
  '🏥 Médical',
  '✈️ Voyage',
  '🍽️ Nourriture',
  '👨‍👩‍👧‍👦 Famille',
  '📖 Verbes',
  '🔢 Nombres',
  '📅 Temps & Dates',
  '💼 Professionnel'
];

const useDecks = () => {
  const [decks, setDecks] = useState(() => {
    // Initialiser immédiatement avec le cache localStorage si disponible
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return [];
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const allNames = new Set();

      // Source 1 : load-all-cards — extrait card.deck (même logique que Otherpages)
      try {
        const res = await fetch(`${N8N_URL}/load-all-cards`);
        const data = await res.json();
        const cards = (data.success && data.cards) ? data.cards : (Array.isArray(data) ? data : []);
        cards.forEach(card => {
          if (card.deck && typeof card.deck === 'string' && card.deck.trim()) {
            allNames.add(card.deck.trim());
          }
        });
        console.log('📚 Decks depuis load-all-cards:', Array.from(allNames));
      } catch (e) {
        console.warn('⚠️ load-all-cards indisponible:', e.message);
      }

      // Source 2 : load-decks — ajoute les decks avec stats
      try {
        const res = await fetch(`${N8N_URL}/load-decks`);
        const data = await res.json();
        if (data.success && Array.isArray(data.decks)) {
          data.decks.forEach(d => {
            if (d.name && d.name.trim()) allNames.add(d.name.trim());
          });
          console.log('📚 Decks depuis load-decks ajoutés, total:', allNames.size);
        }
      } catch (e) {
        console.warn('⚠️ load-decks indisponible:', e.message);
      }

      if (allNames.size > 0) {
        const sorted = Array.from(allNames).sort();
        setDecks(sorted);
        // Persister en cache pour la prochaine session
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(sorted)); } catch { /* ignore */ }
      } else {
        // Essayer le cache localStorage
        try {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.length > 0) {
              setDecks(parsed);
              console.log('📚 Decks depuis cache localStorage:', parsed);
              setIsLoading(false);
              return;
            }
          }
        } catch { /* ignore */ }
        // Dernier recours : liste par défaut
        setDecks(DEFAULT_DECKS);
        console.warn('⚠️ Fallback sur liste par défaut');
      }

      setIsLoading(false);
    };

    load();
  }, []);

  /**
   * Permet d'ajouter un deck personnalisé au cache local
   * (appelé quand l'utilisateur tape un nom de deck manuellement)
   */
  const addCustomDeck = (deckName) => {
    if (!deckName || decks.includes(deckName)) return;
    const updated = [...decks, deckName].sort();
    setDecks(updated);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
  };

  return { decks, isLoading, addCustomDeck };
};

export default useDecks;
